<template>
  <form
    :id="`comment-form-${commentId}`"
    action="post"
    class="comment__edit"
    @submit.prevent="submit"
  >
    <div class="discussion-comment form__group" :class="{ error: errors.has('comment') }">
      <textarea
        ref="textarea"
        v-model="body"
        v-focus
        :style="textareaStyles"
        name="comment"
        placeholder="Add your response"
        data-vv-as="Comment"
        aria-label="Comment"
        @input="resizeTextarea"
      />
      <input-error input-id="comment" />
    </div>

    <div class="discussion-image form__group" :class="{ error: errors.has(imageIdName) }">
      <div class="discussion-image-upload">
        <div class="file-input">
          <span v-if="!hasValidImage" class="iconfont-image" />
          <label v-if="!hasValidImage" class="image" :for="imageIdName"
            ><strong>Attach an Image</strong> (10mb limit)</label
          >
          <input
            :id="imageIdName"
            :ref="imageIdName"
            v-validate="'mimes:image/png,image/jpeg,image/gif|size:10240'"
            :name="imageIdName"
            class="form-control"
            type="file"
            accept="image/png, image/jpeg, image/gif"
            data-vv-validate-on="change|submit"
            @change="onFileChange"
          />
        </div>
        <div v-if="hasValidImage" class="file-remove">
          <button class="btn -image-remove" title="Remove Image" @click.prevent="removeFile">
            <span class="iconfont-menu-close" />Remove Image
          </button>
          <img :src="editImage" alt="" />
        </div>
        <input v-model="removeImage" name="image_remove" type="hidden" />
      </div>
      <input-error :input-id="imageIdName" />
    </div>

    <div class="action__group">
      <button
        class="btn submitting-btn -main -large-medium-only"
        :class="{ submitting: processing }"
        :disabled="processing"
        type="submit"
      >
        <span class="iconfont-loader"></span>
        <span id="form-status" aria-live="polite" role="status">{{
          !processing ? 'Save' : 'Processing...'
        }}</span>
      </button>
      <button
        class="btn -large-medium-only -neutral -gradient-hover"
        :disabled="processing"
        @click="cancelEdit"
      >
        Cancel
      </button>
    </div>
  </form>
</template>

<script>
// todo: how to stop a user from editing a second time before first is stored
// todo: should this thing be in a form?
// @flow
import InputError from '../forms/input-error.vue'

const MINIMUM_TEXTAREA_HEIGHT = 60

export default {
  components: { InputError },
  directives: {
    focus: {
      inserted: (el: Object) => {
        el.focus()
      },
    },
  },
  props: {
    commentId: {
      required: true,
      type: Number,
    },
    parentId: {
      type: Number,
      default: -1,
    },
    bodyOrig: {
      default: '',
      type: String,
    },
    // will be set if user had set image on previous page load
    imageUrl: {
      default: '',
      type: String,
    },
    // will be set if user has upload image on this page load
    // either new comment or edited and replaced image
    imageDataStringOrig: {
      default: '',
      type: String,
    },
  },
  data() {
    return {
      processing: false,
      removeImage: 0,
      body: this.bodyOrig,
      // a string to display in img src
      imageDataString: this.imageDataStringOrig,
      // the form file object, used for submitting
      imageFile: null,
      textareaStyles: 'word-wrap: break-word;',
      mimeTypes: ['image/png', 'image/jpeg', 'image/gif'],
    }
  },
  computed: {
    hasValidImage() {
      return this.editImage // && !this.errors.has('image')
    },
    editImage() {
      return this.imageDataString || this.originalImage
    },
    imageIdName() {
      return `image-edit-field-${this.commentId}`
    },
    originalImage() {
      if (this.removeImage) {
        return ''
      }
      return this.imageDataStringOrig || this.imageUrl
    },
    imageChanged() {
      // removeImage = true if remove or replace, imageFile = true if new or replace image
      return this.removeImage || this.imageFile
    },
  },

  created() {
    // custom validation messages for comment images
    this.$validator.updateDictionary({
      en: {
        messages: {
          mimes: `Only ${this.mimeTypes.join(', ').replace(/image\//g, '')} supported`,
          size: 'Must be an image under 10MB',
        },
      },
    })
  },

  mounted: function() {
    // force resize text area on load for the first time
    const { textarea } = this.$refs

    if (textarea.scrollHeight > MINIMUM_TEXTAREA_HEIGHT) {
      const maxHeight = textarea.scrollHeight <= 280 ? textarea.scrollHeight : 280
      textarea.style.height = `${Math.max(maxHeight, MINIMUM_TEXTAREA_HEIGHT)}px`
    }
  },

  methods: {
    // TODO: make a new resize text area component, see comment-form as it is duplicated
    resizeTextarea() {
      const { textarea } = this.$refs
      const initialScrollHeight = textarea.scrollHeight
      const initialTextareaHeight = parseInt(textarea.style.height || 0)

      // @TODO make MINIMUM_TEXTAREA_HEIGHT dynamic
      if (
        initialScrollHeight < initialTextareaHeight &&
        initialScrollHeight >= MINIMUM_TEXTAREA_HEIGHT
      ) {
        textarea.style.height = '0px'
      }
      // Cap @ 8 lines
      // @TODO make this dynamic as well...
      if (textarea.scrollHeight < 280) {
        textarea.style.height = `${Math.max(textarea.scrollHeight, MINIMUM_TEXTAREA_HEIGHT)}px`
      }
    },

    async submit() {
      if (!this.processing) {
        this.processing = true

        // process validation
        try {
          // do not process if values did not change
          if (this.body === this.bodyOrig && !this.imageChanged) {
            this.$validator.errorBag.add(
              'comment',
              'Comment or image not edited, please edit the comment before saving',
            )
            this.processing = false
            return
          }

          // must have comment OR image OR both to proceed
          if (!this.body.trim() && !this.imageFile) {
            this.$validator.errorBag.add('comment', 'Comment or image required')
            this.processing = false
            return
          }

          // remove comment / image error
          this.$validator.errorBag.remove('comment')

          // any other validation errors
          const result = await this.$validator.validateAll()

          // success, see action as it will keep form in loading state
          // until success, then revert back to comment view (editing the isEditing field)
          if (result) {
            await this.$store.dispatch('submitEditComment', {
              id: this.commentId,
              parentId: this.parentId,
              body: this.body,
              imageFile: this.imageFile,

              /**
               * Cases on handling files             |  action   | image_remove | image
               * 1) no image before, no change       | nothing   | 0            | ''
               * 2) no image before, new image       | add       | 0            | 'abc.jpg'
               * 4) image before, no change          | nothing   | 0            | ''
               * 5) image before, removed            | remove    | 1            | ''
               * 6) image before, new image          | replace   | 1            | 'abc.jpg'
               */
              removeImage: this.removeImage,
            })

            // close edit form
            this.cancelEdit()
          }

          // reset form if not success
          this.processing = false
        } catch (e) {
          // failed, reset form state
          this.processing = false
        }
      }
    },

    cancelEdit() {
      // Note: I am assuming cancel destroys the vue component
      // along with any local variables (like image file) and changes
      this.$emit('cancel-edit')
    },

    onFileChange(e: any) {
      const target = e.target || e.dataTransfer
      const files = target && target.files

      if (!files.length || this.errors.has(this.imageIdName)) {
        return
      }

      // do not render if not supported image
      if (!this.mimeTypes.includes(files[0].type)) {
        return
      }

      this.createImage(files[0])
    },

    createImage(file: any) {
      this.imageFile = file

      const reader = new window.FileReader()
      reader.onload = e => {
        this.imageDataString = e.target.result
      }
      reader.readAsDataURL(file)
    },

    removeFile() {
      this.$refs[this.imageIdName].value = ''
      this.imageDataString = ''
      this.imageFile = null

      // set a flag to tell server to remove the file.  Only do if comment on server has image
      if (this.originalImage) {
        this.removeImage = 1
      }
    },
  },
}
</script>
